/**
 * Organization List Component
 *
 * Displays a list of organizations with actions.
 */

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function OrganizationList({ organizations, onOrganizationClick, onRefresh }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const handleMenuOpen = (event, org) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrg(org);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrg(null);
  };

  const handleView = () => {
    if (selectedOrg) {
      navigate(`/admin/organizations/${selectedOrg.id}`);
    }
    handleMenuClose();
  };

  const handleImpersonate = () => {
    if (selectedOrg) {
      navigate(`/admin/organizations/${selectedOrg.id}/impersonate`);
    }
    handleMenuClose();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Active' },
      suspended: { color: 'warning', icon: <CancelIcon fontSize="small" />, label: 'Suspended' },
      inactive: { color: 'default', icon: <RemoveIcon fontSize="small" />, label: 'Inactive' }
    };

    const config = statusConfig[status] || statusConfig.inactive;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!organizations || organizations.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No organizations found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Organization</strong></TableCell>
              <TableCell><strong>Subdomain</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Users</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => (
              <TableRow
                key={org.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onOrganizationClick(org.id)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {org.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {org.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.slug}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {org.subdomain}.app.ailyce.ai
                  </Typography>
                </TableCell>
                <TableCell>
                  {getStatusChip(org.status)}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {org.user_count || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(org.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, org);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleImpersonate}>
          <PersonAddIcon fontSize="small" sx={{ mr: 1 }} />
          Impersonate User
        </MenuItem>
      </Menu>
    </>
  );
}
